import { useForm } from "react-hook-form";
import { useUserStore } from "@/src/stores/userStore";
import { zodResolver } from "@hookform/resolvers/zod";
import { useStepsStore } from "./state";
import { emptyOfferForm, OfferForm } from "./offer.schema";
import { Form } from "@/src/components/ui/form";
import { useEmit, useSubscribe } from "@/src/lib/actions";
import { ChangeEvent, useCallback, useEffect, useState } from "react";
import { Customer } from "./Customer";
import { Description } from "./Description";
import { Yachts } from "./Yachts";
import { Summary } from "./Summary";
import { Preview } from "./Preview";
import { BuilderHeader } from "./BuilderHeader";
import { Offer, OfferYacht } from "../model";
import { useToast } from "@/src/components/ui/use-toast";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/src/components/ui/dialog";
import { CopyIcon, MailIcon, PlusIcon, Trash2Icon } from "lucide-react";
import { Button } from "@/src/components/ui/button";
import { Input } from "@/src/components/ui/input";
import { z, ZodError } from "zod";
import { calculateCurrencyPrice, cn, copyToClipboard } from "@/src/lib/utils";
import { YachtsModal } from "./YachtsModal";
import { useOffersStore } from "../../../stores/offersStore";
import { Currency } from "../../Currencies/model";
import { fetch } from "@/src/lib/fetch";
import {
  ChangeYachtDetailsModal,
  Loader,
  parseDateIntoDate,
  parseDateIntoTime,
} from "./ChangeYachtDetailsModal/ChangeYachtDetailsModal";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { logOfferCreatorAction } from "../offerCreatorLogs";

export type EditOfferFormProps = {
  offer?: Offer;
  onSubmit?: (offer: OfferForm, emails: string[]) => void;
  onDraft?: (offer: OfferForm) => void;
  onSaveForPreview?: (offer: OfferForm) => void;
};
export function EditOfferForm({ offer, onSubmit: _onSubmit, onDraft, onSaveForPreview }: EditOfferFormProps) {
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [yachtsModalOpen, setYachtsModalOpen] = useState(false);
  const [offerFormData, setOfferFormData] = useState<OfferForm | null>(null);
  const [changeDetailsOpen, setChangeDetailsOpen] = useState(false);
  const [yachtDetailsId, setYachtDetailsId] = useState<null | string>(null);
  const [yachtDetailsIndex, setYachtDetailsIndex] = useState<null | number>(null);
  const [quickAddId, setQuickAddId] = useState<null | string>(null);
  const [quickAddDates, setQuickAddDates] = useState<{ from: string; to: string } | null>(null);
  const [quickAddFallback, setQuickAddFallback] = useState<{ name?: string; media?: string[]; slug?: string } | null>(
    null,
  );
  const {
    currencies,
    setCurrencies,
    offerCurrencyCode,
    setOfferCurrencyCode,
    setOfferYachts,
    yachtsFilters,
    setYachtsFilters,
    offerYachts: offerYachtsStore,
  } = useOffersStore();
  const emit = useEmit<string>("offer:changeDetailsOpen");

  function fetchCurrencies() {
    return fetch<{ data: Currency[] }>("/api/currencies").then((res) => setCurrencies(res.data ?? []));
  }

  useEffect(() => {
    if (!currencies?.length) {
      fetchCurrencies();
    }
  }, []);

  const { step, setStep } = useStepsStore();

  const parsedOfferYachts = offer?.yachts?.map((y) => ({
    ...y,
    ignoreAvailability: y.ignoreAvailability ?? false,
    startDate: parseDateIntoDate(y.startDate),
    endDate: parseDateIntoDate(y.endDate),
    startTime: parseDateIntoTime(y.startDate),
    endTime: parseDateIntoTime(y.endDate),
    pricing: {
      ...y.pricing,
      // those values were missing from the previous iterations, need to add them as 0 as fallback
      extrasPriceInOperatorCurrency: y.pricing?.extrasPriceInOperatorCurrency ?? 0,
      operatorTotalPrice: y.pricing?.operatorTotalPrice ?? 0,
      discountPercentage: y.pricing?.discountPercentage ?? 0,
    },
  }));

  const currentUser = useUserStore((s) => s.user);

  // Prefill defaults (broker workspace): booking owner -> the logged-in broker;
  // title -> "<country> yacht charter" from the enquiry region when we have one
  // and the offer isn't already titled.
  const isExisting = !!(offer as any)?.id;
  const regionCountry = ((offer as any)?.regionCountry ?? (offer as any)?.startLocation ?? "")
    .toString()
    .split(",")[0]
    .trim();
  const derivedTitle = regionCountry ? `${regionCountry} yacht charter` : "";
  const defaultBroker =
    offer?.broker && (offer.broker as any).id
      ? offer.broker
      : currentUser
        ? {
            id: Number((currentUser as any).databaseId ?? currentUser.id),
            // The broker option renders `firstName + " " + lastName`, so include them —
            // fullName alone left the field showing "undefined undefined" on new offers
            // and on deals whose HubSpot owner isn't one of our brokers.
            firstName: currentUser.firstName,
            lastName: currentUser.lastName,
            fullName:
              (currentUser as any).fullName ||
              [currentUser.firstName, currentUser.lastName].filter(Boolean).join(" "),
            email: currentUser.email,
          }
        : undefined;

  const defaultValues = {
    customer: {
      ...emptyOfferForm.customer,
      ...(offer?.customer?.email && { email: offer.customer.email }),
      ...(offer?.customer?.firstName && { firstName: offer.customer.firstName }),
      ...(offer?.customer?.lastName && { lastName: offer.customer.lastName }),
      ...(offer?.customer?.fullName && { fullName: offer.customer.fullName }),
      ...(offer?.customer?.mobile && { mobile: offer.customer.mobile }),
    },
    description: {
      ...emptyOfferForm.description,
      ...((isExisting ? offer?.title : offer?.title || derivedTitle) && {
        title: isExisting ? offer?.title : offer?.title || derivedTitle,
      }),
      ...(offer?.intro && { intro: offer.intro }),
      ...(offer?.brand && { brand: offer.brand }),
      ...(offer?.currency && { currency: offer.currency?.code }),
      ...(offer?.charterType && { charterType: offer.charterType }),
      ...(defaultBroker && { broker: defaultBroker }),
      ...(offer?.guests && { guests: +offer.guests }),
    },
    yachts: !!offer?.yachts?.length ? parsedOfferYachts : emptyOfferForm.yachts,
    hubspotId: offer?.hubspotId,
    mailing: {
      ...emptyOfferForm.mailing,
      ...(offer?.mailingSettings?.bookingConfirmation && {
        bookingConfirmation: offer.mailingSettings.bookingConfirmation,
      }),
      ...(offer?.mailingSettings?.bookingUpdates && {
        bookingUpdates: offer.mailingSettings.bookingUpdates,
      }),
      ...(offer?.mailingSettings?.paymentConfirmations && {
        paymentConfirmations: offer.mailingSettings.paymentConfirmations,
      }),
      ...(offer?.mailingSettings?.paymentReminders && {
        paymentReminders: offer.mailingSettings.paymentReminders,
      }),
    },
  };

  const form = useForm<OfferForm>({
    resolver: zodResolver(OfferForm),
    defaultValues: defaultValues,
    mode: "onTouched",
    reValidateMode: "onChange",
  });

  const charterType = form.watch("description.charterType") as "bareboat" | "skippered" | "crewed";
  const broker = form.watch("description.broker");

  useEffect(() => {
    if (charterType) {
      setYachtsFilters({
        ...yachtsFilters,
        charterType: [charterType],
      });
    }
  }, [charterType]);

  const { toast } = useToast();
  const onSubmit = useCallback(
    form.handleSubmit(
      (data) => {
        // not sure if thats the best idea, but currently it wont work without it
        // we need to filter already existed custom amenities and then add them to the body,
        // cause at the be we remove the offer yacht and its relations
        const currentAmenitiesDetails = data?.yachts?.map((yt) => yt.amenitiesDetails);
        const currentCustomAmenities = data.yachts?.map((yt) =>
          yt.yacht.amenities?.filter((am: any) => am.isCustomAmenity),
        );

        const newData = data.yachts?.map((yacht, i) => {
          const currentDetails = currentAmenitiesDetails[i];
          const currentCustoms = currentCustomAmenities[i];
          const parsedCustoms = currentCustoms.map((cc: any) => {
            const currentDetailsAmenity = currentDetails?.find((d) => d.id === cc.id);
            const currencyId = currencies?.find((c) => c.code === (currentDetailsAmenity?.currency || "EUR"))?.id;

            return {
              ...cc,
              obligatory: currentDetailsAmenity?.obligatory,
              checked: currentDetailsAmenity?.checked,
              finalBookingPrice: currentDetailsAmenity?.price,
              paidLocally: currentDetailsAmenity?.paidLocally,
              currencyCode: currentDetailsAmenity?.currency,
              text: currentDetailsAmenity?.text,
              description: currentDetailsAmenity?.description,
              provider: currentDetailsAmenity?.provider,
              number: currentDetailsAmenity?.number,
              yachtId: yacht.yacht.id,
              currencyId,
              hidden: currentDetailsAmenity?.hidden || false,
              freeOfCharge: currentDetailsAmenity?.freeOfCharge || false,
              yachtIndex: i,
            };
          });
          return {
            ...yacht,
            customAmenities: [...parsedCustoms, ...(yacht.customAmenities ?? [])],
          };
        });
        setOfferFormData({ ...data, yachts: newData });
        setSaveModalOpen(true);
      },
      (errors) => {
        console.error("FORM ERROR", errors);
        if (errors) {
          toast({
            title: "Failed to add offer",
            description: "The form is missing required fields",
            variant: "destructive",
          });

          setStep(
            errors.description && !errors.customer && !errors.yachts
              ? "Description"
              : errors.yachts && !errors.customer
                ? "Yachts"
                : "Customer",
          );
        }
      },
    ),
    [form, currencies],
  );

  const currencyCode = form.watch("description.currency");
  const offerYachts = form.watch("yachts");

  useEffect(() => {
    // each time the broker changes the offer currency and there are yachts added in a different currency we need to recalculate that offer price
    if (offerCurrencyCode && currencyCode !== offerCurrencyCode) {
      if (!!offerYachts?.length) {
        const previousYachtCurrency = currencies?.find((c) => c.code === offerCurrencyCode);
        const newYachtCurrency = currencies?.find((c) => c.code === currencyCode);

        const newOfferYachts = [...offerYachts]?.map((y) => {
          const price = y.pricing;
          const yachtCurrencyCode = price.yachtCurrencyCode;
          const totalPrice = calculateCurrencyPrice(newYachtCurrency!, previousYachtCurrency!, price?.totalPrice); // total price
          const originalPrice = calculateCurrencyPrice(newYachtCurrency!, previousYachtCurrency!, price?.originalPrice); // original price
          const clientPrice = calculateCurrencyPrice(newYachtCurrency!, previousYachtCurrency!, price?.clientPrice); // client price
          const clientPriceApi = calculateCurrencyPrice(
            newYachtCurrency!,
            previousYachtCurrency!,
            price?.clientPriceApi,
          ); // client price api
          const depositPrice = calculateCurrencyPrice(newYachtCurrency!, previousYachtCurrency!, price?.depositPrice); // deposit price
          const helmCustomDiscountPrice = calculateCurrencyPrice(
            newYachtCurrency!,
            previousYachtCurrency!,
            price?.helmCustomDiscountPrice,
          ); // helm custom discount price
          const discountPrice = calculateCurrencyPrice(newYachtCurrency!, previousYachtCurrency!, price?.discountPrice); // price discount for helm on operator now, needs to be discussed
          // price discount for helm on operator now, needs to be discussed
          const commissionPostHelmDiscountPrice = calculateCurrencyPrice(
            newYachtCurrency!,
            previousYachtCurrency!,
            price?.commissionPostHelmDiscountPrice,
          ); // price discount for helm on operator now, needs to be discussed
          const advanceExtras = price?.advanceExtras?.map(({ text, price }) => ({
            text,
            price: calculateCurrencyPrice(newYachtCurrency!, previousYachtCurrency!, price),
          }));
          const advanceObligatory = price?.advanceObligatory?.map(({ text, price }) => ({
            text,
            price: calculateCurrencyPrice(newYachtCurrency!, previousYachtCurrency!, price),
          }));
          const pickupExtras = price?.pickupExtras?.map(({ text, price }) => ({
            text,
            price: calculateCurrencyPrice(newYachtCurrency!, previousYachtCurrency!, price),
          }));
          const pickupObligatory = price?.pickupObligatory?.map(({ text, price }) => ({
            text,
            price: calculateCurrencyPrice(newYachtCurrency!, previousYachtCurrency!, price),
          }));
          const includedFree = price?.includedFree?.map(({ text }) => ({
            text,
          }));
          const amenitiesDetails = y.amenitiesDetails?.map((am) => ({
            ...am,
            price: calculateCurrencyPrice(newYachtCurrency!, previousYachtCurrency!, am.price),
          }));
          const discountCharterPrice = calculateCurrencyPrice(
            newYachtCurrency!,
            previousYachtCurrency!,
            y.discountCharterPrice,
          );
          return {
            ...y,
            discountCharterPrice,
            amenitiesDetails,
            pricing: {
              depositPrice,
              discountPrice,
              originalPrice,
              clientPrice,
              clientPriceApi,
              helmCustomDiscountPrice,
              totalPrice,
              operatorPayablePrice: price.operatorPayablePrice,
              operatorTotalPrice: price.operatorTotalPrice,
              extrasPriceInOperatorCurrency: price.extrasPriceInOperatorCurrency,
              commissionPostHelmDiscountPrice,
              advanceExtras,
              advanceObligatory,
              pickupExtras,
              pickupObligatory,
              includedFree,
              discountPercentage: price.discountPercentage,
              yachtCurrencyCode,
            },
          };
        });
        form.setValue("yachts", newOfferYachts);
      }
    }
    setOfferCurrencyCode(currencyCode);
  }, [currencyCode]);

  const handleDraft = () => {
    form.trigger("customer").then((res) => {
      if (res) {
        const data = form.getValues();
        onDraft?.(data);
      } else {
        setStep("Customer");
      }
    });
  };

  const handlePreviewSave = () => {
    form.trigger("customer").then((res) => {
      if (res) {
        onSaveForPreview?.(form.getValues());
      } else {
        setStep("Customer");
      }
    });
  };

  useSubscribe("offer:save", onSubmit);
  useSubscribe("offer:draft", handleDraft);
  useSubscribe("offer:previewSave", handlePreviewSave);
  useSubscribe("offer:openYachtsModal", () => setYachtsModalOpen(true));
  useSubscribe("offer:closeYachtModal", () => setYachtsModalOpen(false));

  const handleClick = (id: string, index?: number) => {
    setYachtDetailsId(id);
    setYachtDetailsIndex(index !== null && index !== undefined ? index : null);
    setChangeDetailsOpen(true);
    setYachtsModalOpen(false);
    logOfferCreatorAction({
      eventType: "offer_yacht.modal_opened",
      offerId: offer?.id,
      meta: {
        yacht_id: id,
        index: index ?? null,
        mode: index !== null && index !== undefined ? "edit" : "add",
      },
    });
  };

  const handleQuickAdd = (id: string, fallback?: { name?: string; media?: string[]; slug?: string }) => {
    // Auto-add needs dates to price the yacht; with no dates set it silently does
    // nothing. Open the full edit modal instead so the broker can set the dates,
    // times and location (and tick "Ignore availability") — this lets a yacht that
    // isn't showing as available for an unset period still be added to the offer.
    if (!yachtsFilters?.checkIn || !yachtsFilters?.checkOut) {
      handleClick(id);
      return;
    }
    logOfferCreatorAction({
      eventType: "offer_yacht.modal_opened",
      offerId: offer?.id,
      meta: { yacht_id: id, index: null, mode: "quick-add" },
    });
    setQuickAddFallback(fallback ?? null);
    setQuickAddId(id);
  };

  // Add a yacht straight from the calendar view for the week the broker selected.
  // Pass the dates directly to the auto-add modal (presetCheckIn/Out) rather than
  // via setYachtsFilters — SearchBar re-syncs its form on a filter change and its
  // onSubmit only re-emits *dirty* fields, which would drop the dates we just set
  // and leave the yacht with no period (it then reports "no live price").
  const handleAddWithDates = (id: string, from: string, to: string) => {
    setQuickAddDates({ from, to });
    handleQuickAdd(id);
  };

  const onQuickAdded = (yacht: OfferYacht) => {
    const yachts = [...offerYachtsStore];
    logOfferCreatorAction({
      eventType: "offer_yacht.save",
      offerId: offer?.id,
      meta: { offer_yacht_id: yacht.id, yacht_id: yacht.yacht?.id, index: yachts.length, mode: "quick-add" },
    });
    // The on-demand detail fetch can return an empty name/media for yachts not
    // currently in the provider API — backfill from the search result so the
    // shortlist/preview always shows something.
    const fb = quickAddFallback;
    const mergedYacht = yacht.yacht
      ? {
          ...yacht.yacht,
          name: yacht.yacht.name || fb?.name || "",
          slug: (yacht.yacht as any).slug || fb?.slug || "",
          media: yacht.yacht.media?.length ? yacht.yacht.media : (fb?.media ?? []),
        }
      : yacht.yacht;
    // autoAdd fetches live pricing, but only CYA/NS/BM do a true real-time
    // availability check — Yachtfolio (and internal Helm/Ankor) don't.
    const liveAvailabilityProviders = ["CYA", "NS", "BM"];
    const checked = liveAvailabilityProviders.includes((yacht.yacht as any)?.provider);
    const stamped = {
      ...yacht,
      yacht: mergedYacht,
      ignoreAvailability: yacht.ignoreAvailability ?? false,
      availabilityChecked: checked,
      checkedAt: checked ? new Date().toISOString() : null,
    };
    const next = [...yachts, stamped];
    form.setValue("yachts", next);
    setOfferYachts(next);
    setQuickAddId(null);
    setQuickAddFallback(null);
    setQuickAddDates(null);
    toast({ title: "Added to offer", description: mergedYacht?.name || undefined });
  };

  const onAddToCartClick = (yacht: OfferYacht) => {
    let yachts = [...offerYachtsStore];
    logOfferCreatorAction({
      eventType: "offer_yacht.save",
      offerId: offer?.id,
      meta: {
        offer_yacht_id: yacht.id,
        yacht_id: yacht.yacht?.id,
        index: yachtDetailsIndex ?? yachts.length,
        mode: yachtDetailsIndex !== null ? "edit" : "add",
      },
    });
    if (yachtDetailsIndex !== null) {
      yachts[yachtDetailsIndex] = yacht;
      form.setValue("yachts", yachts);
      setOfferYachts(yachts);
    } else {
      form.setValue("yachts", [...yachts, { ...yacht }]);
      setOfferYachts([...yachts, { ...yacht }]);
    }
    setChangeDetailsOpen(false);
  };

  return (
    <>
      <Form {...form}>
        <form className="flex flex-col gap-6" onSubmit={onSubmit}>
          <BuilderHeader offerId={offer?.id} />
          <Dialog open={step === "Customer"} onOpenChange={(o) => !o && setStep("Yachts")}>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Select a client</DialogTitle>
              </DialogHeader>
              <Customer offerId={offer?.id} />
              <div className="flex justify-end mt-2">
                <Button type="button" onClick={() => setStep("Yachts")}>
                  Done
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          <Dialog open={step === "Description"} onOpenChange={(o) => !o && setStep("Yachts")}>
            <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Offer details</DialogTitle>
              </DialogHeader>
              <Description offerId={offer?.id} />
              <div className="flex justify-end mt-2">
                <Button type="button" onClick={() => setStep("Yachts")}>
                  Done
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          {(step === "Yachts" || step === "Customer" || step === "Description") && (
            <Yachts openChangeDetails={handleClick} onQuickAdd={handleQuickAdd} onAddWithDates={handleAddWithDates} />
          )}
          {step === "Summary" && (
            <Summary openChangeDetails={handleClick} dealLink={offer?.dealLink} offerId={offer?.id} />
          )}
          {step === "Preview" && <Preview offerId={offer?.id} />}
        </form>
        {changeDetailsOpen && (
          <ChangeYachtDetailsModal
            offerId={offer?.id}
            onAddClick={onAddToCartClick}
            id={yachtDetailsId}
            open={changeDetailsOpen}
            setOpen={setChangeDetailsOpen}
            onCancel={() => {
              logOfferCreatorAction({
                eventType: "offer_yacht.modal_cancelled",
                offerId: offer?.id,
                meta: {
                  yacht_id: yachtDetailsId,
                  index: yachtDetailsIndex,
                  mode: yachtDetailsIndex !== null ? "edit" : "add",
                },
              });
            }}
            index={yachtDetailsIndex}
          />
        )}
        {quickAddId && (
          <ChangeYachtDetailsModal
            key={quickAddId}
            offerId={offer?.id}
            onAddClick={onQuickAdded}
            id={quickAddId}
            open={false}
            presetCheckIn={quickAddDates?.from}
            presetCheckOut={quickAddDates?.to}
            setOpen={() => {
              setQuickAddId(null);
              setQuickAddDates(null);
            }}
            onUnavailable={() => {
              setQuickAddId(null);
              setQuickAddDates(null);
              toast({
                title: "Couldn't add yacht",
                description: "No live price/availability came back for the selected dates.",
                variant: "destructive",
              });
            }}
            index={null}
            autoAdd
          />
        )}
      </Form>
      {offerFormData && (
        <SaveOfferModal
          id={offer?.id}
          onSubmit={() => _onSubmit?.(offerFormData, [broker.email])}
          modalOpen={saveModalOpen}
          setModalOpen={setSaveModalOpen}
          brand={offerFormData?.description?.brand}
        />
      )}
      <YachtsModal
        setValue={form.setValue}
        open={yachtsModalOpen}
        setOpen={setYachtsModalOpen}
        currency={form.getValues("description.currency")}
        editYacht={(id: string, index: number) => handleClick(id, index)}
      />
    </>
  );
}

export function EmailsModal({
  modalOpen,
  setModalOpen,
  onSubmit,
  hideSaveBtn,
  defaultEmail,
}: {
  modalOpen: boolean;
  setModalOpen: (open: boolean) => void;
  onSubmit: (emails: string[]) => void;
  hideSaveBtn?: boolean;
  defaultEmail?: string;
}) {
  const [emails, setEmails] = useState<{ email: string }[]>([
    {
      email: defaultEmail ?? "",
    },
  ]);
  const [errors, setErrors] = useState<ZodError["errors"] | null>(null);

  const emailValidation = z.array(
    z.object({
      email: z.string().email(),
    }),
  );

  const validateEmails = () => {
    try {
      emailValidation.parse(emails);
      onSubmit(emails.map(({ email }) => email));
    } catch (error: unknown) {
      const knownError = error as ZodError;
      setErrors(knownError["errors"]);
    }
  };

  const save = () => {
    try {
      onSubmit([]);
    } catch (error: unknown) {
      const knownError = error as ZodError;
      setErrors(knownError["errors"]);
    }
  };

  const onClose = () => {
    setModalOpen(false);
    setEmails([
      {
        email: "",
      },
    ]);
    setErrors(null);
  };

  const removeInput = (index: number) => {
    const copiedMails = [...emails];
    copiedMails.splice(index, 1);
    setEmails(copiedMails);
    setErrors(null);
  };

  const closeModal = (e: boolean) => {
    setModalOpen(e);
    onClose();
  };

  return (
    <Dialog open={modalOpen} onOpenChange={(e) => closeModal(e)}>
      <DialogContent className="max-w-[424px]">
        <DialogHeader>
          <DialogTitle className="text-xl flex flex-col">Enter e-mails</DialogTitle>
          <p className="text-slate-500 text-sm mt-2">
            Enter the e-mails you’d like the offer to be send to. You can send it to yourself or to the Customer.
          </p>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          {emails?.map(({ email }, i) => (
            <div key={i} className="flex items-center w-full gap-2">
              <EmailInput
                error={errors?.find((e) => e.path?.[0] === i)}
                value={email}
                onChange={(e) => {
                  const copy = [...emails];
                  copy[i].email = e.currentTarget.value;
                  setEmails(copy);
                  setErrors(null);
                }}
                label={"Email #" + (i + 1)}
              />
              {!!i && (
                <span className="mt-7 cursor-pointer" onClick={() => removeInput(i)}>
                  <Trash2Icon size={20} />
                </span>
              )}
            </div>
          ))}
        </div>
        <Button
          onClick={() => {
            setEmails([...emails, { email: "" }]);
          }}
          className="ml-auto gap-2"
          variant={"link"}
        >
          Add more e-mails <PlusIcon size={16} />
        </Button>
        <DialogFooter className="mt-4">
          <Button variant="outline" className="mr-auto" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={validateEmails} className="gap-2 ml-auto">
            Send the offer <MailIcon />
          </Button>
          {!hideSaveBtn && (
            <Button onClick={save} className="gap-2 ml-2">
              Save
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const EmailInput = ({
  value,
  onChange,
  label,
  error,
}: {
  label: string;
  value: string;
  onChange: (val: ChangeEvent<HTMLInputElement>) => void;
  error?: ZodError["errors"][number];
}) => {
  return (
    <div className="flex flex-col gap-1.5 w-full">
      <span
        className={cn("text-base", {
          "text-red-500": error,
        })}
      >
        {label}
      </span>
      <Input
        className={cn({
          "border-red-500": error,
        })}
        placeholder="Enter email"
        onChange={onChange}
        value={value}
      />
      {error && <span className="text-red-500 text-sm">{error?.message}</span>}
    </div>
  );
};

export function SaveOfferModal({
  modalOpen,
  setModalOpen,
  onSubmit,
  id,
  brand,
}: {
  modalOpen: boolean;
  setModalOpen: (open: boolean) => void;
  onSubmit: () => void;
  id?: string;
  brand?: "helm" | "anchor";
}) {
  const [offerId, setOfferId] = useState(id);
  const [offerSent, setOfferSent] = useState(false);
  const [isSendingOffer, setIsSendigOffer] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const save = async () => {
    setIsSendigOffer(true);
    try {
      const oId: any = await onSubmit();
      setOfferId(oId);
      setOfferSent(true);
      setIsSendigOffer(false);
    } catch (error: unknown) {
      console.log(error, "Error saving offer");
      setIsSendigOffer(false);
    }
  };
  const queryClient = useQueryClient();

  const onClose = async () => {
    setModalOpen(false);
    await queryClient.invalidateQueries({ queryKey: ["yachtsData", offerId] });
  };

  const closeModal = (e: boolean) => {
    setModalOpen(e);
    onClose();
    navigate("/offers");
  };

  const baseUrl = brand === "anchor" ? "https://anchor.yt" : "https://www.helm.yt";

  const offerFrontUrl = !!offerId ? `${baseUrl}/offer/${offerId}` : "";
  return (
    <Dialog open={modalOpen} onOpenChange={(e) => closeModal(e)}>
      <DialogContent className="max-w-[464px]">
        <DialogHeader>
          <DialogTitle className="text-xl flex flex-col">Share this offer</DialogTitle>
        </DialogHeader>
        <p className="text-slate-500 text-sm">Share this offer with the Customer by copying the link.</p>
        <div className="flex flex-col gap-1.5 w-full mt-2">
          <span className={cn("text-base")}>Offer URL</span>
          <Input disabled value={offerFrontUrl ? offerFrontUrl : "Save the offer to see URL"} />
        </div>
        <DialogFooter className="mt-4">
          <Button variant="outline" className="mr-auto" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={!offerFrontUrl}
            onClick={() => {
              copyToClipboard(offerFrontUrl);
              toast({
                title: "Copied!",
                description: "",
                variant: "default",
              });
            }}
            className="gap-2 ml-auto"
          >
            Copy the URL <CopyIcon />
          </Button>
          <Button disabled={offerSent} onClick={save} className="ml-2">
            {isSendingOffer ? (
              <>
                Saving
                <Loader text="" />
              </>
            ) : (
              "Save"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
